import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import Button from '../components/ui/Button'
import DataTable from '../components/ui/DataTable'
import EmptyState from '../components/ui/EmptyState'
import ErrorState from '../components/ui/ErrorState'
import Input from '../components/ui/Input'
import LoadingState from '../components/ui/LoadingState'
import PageHeader from '../components/ui/PageHeader'
import PageShell from '../components/ui/PageShell'
import SectionCard from '../components/ui/SectionCard'
import StatusBadge from '../components/StatusBadge'
import publicRecognitionAdminService from '../services/publicRecognitionAdminService'
import { formatDateTime } from '../utils/date'
import { formatEmptyValue } from '../utils/displayValue'
import { ui } from '../styles/ui'
import { useTranslation } from '../contexts/TranslationContext'

const DEBOUNCE_MS = 350

export default function PublicRecognitionsAdmin() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [items, setItems] = useState([])
  const [summary, setSummary] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [total, setTotal] = useState(0)

  const filters = useMemo(() => ({
    page: Number(searchParams.get('page') || '1'),
    limit: Number(searchParams.get('limit') || '25'),
    status: searchParams.get('status') || '',
    sourceType: searchParams.get('sourceType') || '',
    dateFrom: searchParams.get('dateFrom') || '',
    dateTo: searchParams.get('dateTo') || '',
    search: searchParams.get('search') || '',
    hasError: searchParams.get('hasError') || '',
  }), [searchParams])

  const [searchInput, setSearchInput] = useState(filters.search)

  useEffect(() => {
    setSearchInput(filters.search)
  }, [filters.search])

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput === filters.search) return
      const next = new URLSearchParams(searchParams)
      if (searchInput.trim()) next.set('search', searchInput.trim())
      else next.delete('search')
      next.set('page', '1')
      setSearchParams(next, { replace: true })
    }, DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [filters.search, searchInput, searchParams, setSearchParams])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError('')
    publicRecognitionAdminService.listPublicRecognitions({
      ...filters,
      hasError: filters.hasError === '' ? undefined : filters.hasError === 'true',
    })
      .then((data) => {
        if (cancelled) return
        setItems(Array.isArray(data?.items) ? data.items : [])
        setTotal(Number(data?.total || 0))
        setSummary(data?.summary || null)
      })
      .catch((err) => {
        if (cancelled) return
        setError(err?.response?.data?.message || err.message || t('Failed to load public recognitions', 'Failed to load public recognitions'))
        setItems([])
        setTotal(0)
        setSummary(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [filters])

  function updateFilter(key, value) {
    const next = new URLSearchParams(searchParams)
    if (value == null || value === '') next.delete(key)
    else next.set(key, String(value))
    next.set('page', '1')
    setSearchParams(next, { replace: true })
  }

  function goToPage(page) {
    const safePage = Math.max(1, Number(page) || 1)
    const next = new URLSearchParams(searchParams)
    next.set('page', String(safePage))
    setSearchParams(next, { replace: true })
  }

  const currentPage = Math.max(1, Number(filters.page) || 1)
  const pageSize = Math.max(1, Number(filters.limit) || 25)
  const totalPages = Math.max(1, Math.ceil(total / pageSize))
  const canPrev = currentPage > 1
  const canNext = currentPage < totalPages
  const summaryByStatus = summary?.byStatus || {}
  const summaryByEndpointGroup = summary?.byEndpointGroup || {}
  const summaryBySource = summary?.bySource || {}

  if (loading) return <PageShell><LoadingState text={t('Loading public recognitions...', 'Loading public recognitions...')} /></PageShell>
  if (error) return <PageShell><ErrorState title={t('Failed to load', 'Failed to load')} description={error} /></PageShell>

  return (
    <PageShell>
      <PageHeader
        title={t('Public API Recognitions', 'Public API Recognitions')}
        subtitle={t('Admin diagnostics for public receipt recognition requests', 'Admin diagnostics for public receipt recognition requests')}
        actions={<Button onClick={() => setSearchParams(new URLSearchParams(searchParams))}>{t('Refresh', 'Refresh')}</Button>}
      />

      <SectionCard>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Total jobs', 'Total jobs')}</p>
            <p className="mt-1 text-2xl font-semibold text-foreground">{summary?.total ?? total}</p>
          </div>
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Completed', 'Completed')}</p>
            <p className="mt-1 text-2xl font-semibold text-emerald-700">{summaryByStatus.completed ?? 0}</p>
          </div>
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Failed', 'Failed')}</p>
            <p className="mt-1 text-2xl font-semibold text-red-700">{summaryByStatus.failed ?? 0}</p>
          </div>
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Public API', 'Public API')}</p>
            <p className="mt-1 text-2xl font-semibold text-sky-700">{summaryByEndpointGroup.public_recognition ?? 0}</p>
          </div>
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Telegram', 'Telegram')}</p>
            <p className="mt-1 text-2xl font-semibold text-violet-700">{summaryByEndpointGroup.telegram_mini_app_recognition ?? 0}</p>
          </div>
          <div className="rounded-lg border border-outline bg-surface-elevated p-4">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">{t('Avg duration', 'Avg duration')}</p>
            <p className="mt-1 text-2xl font-semibold text-foreground">
              {summary?.averageDurationMs == null ? formatEmptyValue(null) : `${Math.round(summary.averageDurationMs)} ${t('ms', 'ms')}`}
            </p>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 gap-3 text-sm text-muted-foreground md:grid-cols-3">
          <div className="rounded-lg border border-outline px-4 py-3">
            <p className="text-xs uppercase tracking-wide">{t('Images', 'Images')}</p>
            <p className="mt-1 text-foreground">{summaryBySource.image ?? 0}</p>
          </div>
          <div className="rounded-lg border border-outline px-4 py-3">
            <p className="text-xs uppercase tracking-wide">{t('QR', 'QR')}</p>
            <p className="mt-1 text-foreground">{summaryBySource.qr ?? 0}</p>
          </div>
          <div className="rounded-lg border border-outline px-4 py-3">
            <p className="text-xs uppercase tracking-wide">{t('Speech', 'Speech')}</p>
            <p className="mt-1 text-foreground">{summaryBySource.speech ?? 0}</p>
          </div>
        </div>
      </SectionCard>

      <SectionCard>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <Input placeholder={t('Search by id/request/client', 'Search by id/request/client')} value={searchInput} onChange={(e) => setSearchInput(e.target.value)} />
          <select className={ui.select} value={filters.status} onChange={(e) => updateFilter('status', e.target.value)}>
            <option value="">{t('All statuses', 'All statuses')}</option>
            <option value="pending">{t('pending', 'pending')}</option>
            <option value="processing">{t('processing', 'processing')}</option>
            <option value="completed">{t('completed', 'completed')}</option>
            <option value="failed">{t('failed', 'failed')}</option>
          </select>
          <select className={ui.select} value={filters.sourceType} onChange={(e) => updateFilter('sourceType', e.target.value)}>
            <option value="">{t('All sources', 'All sources')}</option>
            <option value="image">{t('image', 'image')}</option>
            <option value="qr">{t('qr', 'qr')}</option>
            <option value="speech">{t('speech', 'speech')}</option>
          </select>
          <select className={ui.select} value={filters.hasError} onChange={(e) => updateFilter('hasError', e.target.value)}>
            <option value="">{t('All records', 'All records')}</option>
            <option value="true">{t('Errors only', 'Errors only')}</option>
            <option value="false">{t('Without errors', 'Without errors')}</option>
          </select>
          <Input type="date" value={filters.dateFrom} onChange={(e) => updateFilter('dateFrom', e.target.value)} />
          <Input type="date" value={filters.dateTo} onChange={(e) => updateFilter('dateTo', e.target.value)} />
        </div>
      </SectionCard>

      <SectionCard subtitle={`${t('Total', 'Total')}: ${total}`}>
        {items.length === 0 ? (
          <EmptyState title={t('No recognitions found', 'No recognitions found')} description={t('Try adjusting filters and search.', 'Try adjusting filters and search.')} />
        ) : (
          <div className="space-y-3">
            <DataTable
              columns={[
                { key: 'createdAt', label: t('Created', 'Created'), sortable: true, sortAccessor: (row) => row.createdAt || '' },
                { key: 'status', label: t('Status', 'Status'), sortable: true },
                { key: 'sourceType', label: t('Source', 'Source'), sortable: true },
                { key: 'publicApiEndpointGroup', label: t('API group', 'API group'), sortable: true },
                { key: 'confidence', label: t('Confidence', 'Confidence'), sortable: true, sortAccessor: (row) => Number(row.confidence ?? -1) },
                { key: 'durationMs', label: t('Duration', 'Duration'), sortable: true, sortAccessor: (row) => Number(row.durationMs ?? -1) },
                { key: 'clientId', label: t('Client', 'Client'), sortable: true },
                { key: 'artifacts', label: t('Artifacts', 'Artifacts') },
                { key: 'error', label: t('Error', 'Error'), sortable: true },
                { key: 'actions', label: t('Actions', 'Actions') },
              ]}
              rows={items}
              renderRow={(row) => (
                <tr key={row.id} className={ui.tableRow}>
                  <td className={ui.tableCellText}>{formatDateTime(row.createdAt)}</td>
                  <td className={ui.tableCell}><StatusBadge label={t('Status', 'Status')} status={row.status} /></td>
                  <td className={ui.tableCellText}>{row.sourceType}</td>
                  <td className={ui.tableCellText}>{formatEmptyValue(row.publicApiEndpointGroup)}</td>
                  <td className={ui.tableCellText}>{typeof row.confidence === 'number' ? row.confidence.toFixed(3) : formatEmptyValue(null)}</td>
                  <td className={ui.tableCellText}>{typeof row.durationMs === 'number' ? `${row.durationMs} ${t('ms', 'ms')}` : formatEmptyValue(null)}</td>
                  <td className={ui.tableCellText}>{formatEmptyValue(row.clientId)}</td>
                  <td className={ui.tableCellText}>
                    {[
                      row.artifactSummary?.hasImageAsset ? t('image', 'image') : null,
                      row.artifactSummary?.hasQrRaw ? t('qr', 'qr') : null,
                      row.artifactSummary?.hasSpeechRaw ? t('speech', 'speech') : null,
                      row.artifactSummary?.hasJobLogs ? t('logs', 'logs') : null,
                    ].filter(Boolean).join(', ') || formatEmptyValue(null)}
                  </td>
                  <td className={ui.tableCellText}>{formatEmptyValue(row.error || row.terminationReason)}</td>
                  <td className={ui.tableCell}>
                    <Link className="text-primary underline" to={`/admin/public-recognitions/${row.id}`}>{t('View details', 'View details')}</Link>
                  </td>
                </tr>
              )}
            />
            <div className="flex items-center justify-end gap-2">
              <Button onClick={() => goToPage(currentPage - 1)} disabled={!canPrev}>{t('Previous', 'Previous')}</Button>
              <span className="text-sm text-muted-foreground">
                {t('Page', 'Page')} {currentPage} {t('of', 'of')} {totalPages}
              </span>
              <Button onClick={() => goToPage(currentPage + 1)} disabled={!canNext}>{t('Next', 'Next')}</Button>
            </div>
          </div>
        )}
      </SectionCard>
    </PageShell>
  )
}
